import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { Separator } from "../../ui/separator";
import HeaderNavLinks from "./HeaderNavLinks";
import TopUpOverlayDialog from "../TopUpOverlayDialog";
import {
  Globe, Menu, LogIn, LogOut, Settings, Coins, Download, Plus,
  Inbox as InboxIcon, Bell, Bookmark,
} from "lucide-react";

/**
 * HeaderMobileSheet — the hamburger trigger + right-side slide-in sheet
 * for viewports < xl. Hosts the nav links, profile shortcut, dashboard
 * shortcut with embedded credit balance, inbox, install-app prompt
 * (hidden inside the Capacitor native shell), and sign-out.
 *
 * Props:
 *   user         — current auth user
 *   logout       — auth context logout
 *   newFollowers — from useHeaderNotifications
 *   inboxUnread  — from useHeaderNotifications
 *   open         — sheet open state (lifted to parent for "logo tap
 *                  closes everything" behaviour)
 *   onOpenChange — sheet open setter
 *   onOpenAuth   — pops the parent's auth dialog
 */
const HeaderMobileSheet = ({
  user, logout, newFollowers, inboxUnread, expiredAlertsUnread, open, onOpenChange, onOpenAuth,
}) => {
  const close = () => onOpenChange(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const isProducer = user?.subscription_tier === "producer";
  // Track tap-vs-cancel reliably on iOS — pointerdown fires when the
  // finger lands, pointerup fires when it lifts in the same hit zone.
  // Using onClick alone can race with the 300ms tap-delay and Safari
  // ghost-click suppression in WKWebView, which the user has reported
  // as a recurring "menu doesn't open on first tap" glitch even after
  // the prior tap-highlight fix.
  const handleToggle = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onOpenChange(!open);
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <button
        type="button"
        className="text-white p-3 flex items-center justify-center rounded-md hover:bg-white/10 active:bg-white/20 transition-colors"
        style={{
          // Pin the trigger above EVERY other header layer so even if
          // the absolutely-positioned logo wrapper accidentally drifts
          // over the hamburger area, the tap still lands here.
          // `position:relative` is required for `z-index` to apply.
          position: "relative",
          zIndex: 1000,
          // 44×44 iOS minimum tap target (Apple HIG).
          minWidth: "44px",
          minHeight: "44px",
          // Disable double-tap zoom + 300ms tap-delay.
          touchAction: "manipulation",
          // Kill the grey/white iOS WKWebView tap-flash entirely; the
          // hover/active states above provide visible feedback.
          WebkitTapHighlightColor: "transparent",
          WebkitUserSelect: "none",
          userSelect: "none",
          cursor: "pointer",
        }}
        data-testid="mobile-menu-btn"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        onClick={handleToggle}
      >
        <Menu className="h-6 w-6" />
      </button>
      <SheetContent
        side="right"
        className="bg-gray-900 border-gray-800 text-white w-[280px] sm:w-[320px] overflow-y-auto ge-no-anim-sheet"
        style={{
          // Push content below the iPhone notch + match the fixed-header
          // offset so the first menu item is never hidden behind the
          // app header.
          paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)",
          paddingBottom: "env(safe-area-inset-bottom, 1rem)",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <Globe className="h-6 w-6 text-emerald-500" />
            GlobalEyes.TV
          </SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-2 mt-6">
          <HeaderNavLinks user={user} mobile onClose={close} />
          <Separator className="my-4 bg-gray-800" />
          {user ? (
            <>
              {/* Username block. Subtitle clearly identifies the tap
                  target as the "Public Profile" — credits live in the
                  Dashboard link below to avoid mixing identity with
                  wallet state. */}
              <Link
                to={`/${user.username}`}
                className="flex items-center gap-3 py-3 px-4 hover:bg-gray-800 rounded-lg min-h-[56px]"
                style={{ touchAction: "manipulation" }}
                onClick={close}
                data-testid="mobile-username-link"
              >
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user.profile_image} />
                  <AvatarFallback className="bg-emerald-600">
                    {user.username[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-white">@{user.username}</p>
                  <p className="text-xs text-gray-400">Public Profile</p>
                </div>
              </Link>
              <Link
                to="/dashboard"
                className="flex items-center gap-2 py-3 px-4 hover:bg-gray-800 rounded-lg text-gray-300 min-h-[48px]"
                style={{ touchAction: "manipulation" }}
                onClick={close}
              >
                <Settings className="h-4 w-4" /> Dashboard
                {/* Credits live here now — clear association with the
                    wallet UI inside the dashboard. */}
                <span className="ml-auto flex items-center gap-1 text-emerald-400 text-xs font-semibold tabular-nums" data-testid="mobile-credit-balance">
                  <Coins className="h-3 w-3" />
                  {user.credits.toFixed(2)}
                </span>
                {newFollowers > 0 && (
                  <span data-testid="new-followers-badge-mobile" className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center">
                    +{newFollowers > 99 ? "99" : newFollowers}
                  </span>
                )}
              </Link>
              {isProducer && (
                <button
                  onClick={() => { close(); setTopUpOpen(true); }}
                  className="flex items-center gap-2 py-3 px-4 hover:bg-gray-800 rounded-lg text-emerald-300 w-full text-left min-h-[48px]"
                  style={{ touchAction: "manipulation" }}
                  data-testid="mobile-topup-btn"
                >
                  <Plus className="h-4 w-4" /> Top-up Credits
                </button>
              )}
              <Link
                to="/inbox"
                className="flex items-center gap-2 py-3 px-4 hover:bg-gray-800 rounded-lg text-gray-300 min-h-[48px]"
                style={{ touchAction: "manipulation" }}
                onClick={close}
                data-testid="nav-inbox-mobile"
              >
                <InboxIcon className="h-4 w-4" /> Inbox
                {inboxUnread > 0 && (
                  <span data-testid="inbox-unread-badge-mobile" className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full bg-emerald-500 text-black text-xs font-bold flex items-center justify-center">
                    {inboxUnread > 99 ? "99+" : inboxUnread}
                  </span>
                )}
              </Link>
              <Link
                to="/marketplace/expired"
                className="flex items-center gap-2 py-3 px-4 hover:bg-gray-800 rounded-lg text-gray-300 min-h-[48px]"
                style={{ touchAction: "manipulation" }}
                onClick={close}
                data-testid="nav-expired-alerts-mobile"
              >
                <Bell className="h-4 w-4 text-amber-300" /> Expired Market
                {expiredAlertsUnread > 0 && (
                  <span data-testid="expired-alerts-badge-mobile" className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full bg-amber-400 text-black text-xs font-bold flex items-center justify-center animate-pulse">
                    {expiredAlertsUnread > 99 ? "99+" : expiredAlertsUnread}
                  </span>
                )}
              </Link>
              <Link
                to="/trail"
                className="flex items-center gap-2 py-3 px-4 hover:bg-gray-800 rounded-lg text-gray-300 min-h-[48px]"
                style={{ touchAction: "manipulation" }}
                onClick={close}
                data-testid="nav-trail-mobile"
              >
                <Bookmark className="h-4 w-4" /> My Trail
              </Link>
              {/* Install-app prompt is hidden inside the native Capacitor
                  shell — by definition the user already has the app
                  installed. */}
              {!(typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) && (
                <button
                  onClick={() => {
                    close();
                    window.dispatchEvent(new CustomEvent("globaleyes:open-install-prompt"));
                  }}
                  className="flex items-center gap-2 py-3 px-4 hover:bg-gray-800 rounded-lg text-gray-300 w-full text-left min-h-[48px]"
                  style={{ touchAction: "manipulation" }}
                  data-testid="install-app-mobile"
                >
                  <Download className="h-4 w-4" /> Install app on phone
                </button>
              )}
              <button
                onClick={() => { logout(); close(); }}
                className="flex items-center gap-2 py-3 px-4 hover:bg-gray-800 rounded-lg text-red-400 w-full text-left min-h-[48px]"
                style={{ touchAction: "manipulation" }}
              >
                <LogOut className="h-4 w-4" /> Sign Out
              </button>
            </>
          ) : (
            <>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 w-full mt-2"
                onClick={() => { close(); onOpenAuth(); }}
                data-testid="mobile-get-started-btn"
              >
                <LogIn className="h-4 w-4 mr-2" /> Get Started
              </Button>
              {/* Cold-traffic alternative — visitors who landed from
                  X / a share-card and aren't ready to create an
                  account yet can still drop their email here so we
                  can re-engage them when their city opens up. */}
              <Link
                to="/waitlist"
                onClick={close}
                data-testid="mobile-join-waitlist-link"
                className="text-center text-gray-400 hover:text-emerald-300 text-xs mt-3 block"
              >
                Not ready yet? <span className="underline">Join the waitlist</span>
              </Link>
            </>
          )}
        </div>
      </SheetContent>
      <TopUpOverlayDialog open={topUpOpen} onOpenChange={setTopUpOpen} />
    </Sheet>
  );
};

export default HeaderMobileSheet;
